/**
 * We take the approach of organizing each “cast member” in a scene within its own file.
 * This is a choice and isn’t required, but it does isolate internal changes/enhancements
 * to these specific “characters.”
 */
import {
  BufferGeometry,
  DoubleSide,
  BufferAttribute,
  MeshPhongMaterial,
  Mesh,
  Group, //from ship
  BoxGeometry
} from 'three' // from garnet
//import { Tween, Ease } from '@createjs/tweenjs'
//import {setOpacity} from '../utilities/utilities'

/**
 * The use of a JavaScript “class” is also a design choice.
 */

const BASE_VERTICES = [
  [0, 1, 0.5], // 0
  [0.5, 0.9, 0.5], // 1
  [0.9, 0.5, 0.5], //2 right side
  [1, 0, 0.5], //3
  [0.9, -0.5, 0.5], //4
  [0.5, -0.9, 0.5], //5
  [0, -1, 0.5], //6
  [-0.5, -0.9, 0.5], //7
  [-0.9, -0.5, 0.5], //8
  [-1, 0, 0.5], //9
  [-0.9, 0.5, 0.5], //10
  [-0.5, 0.9, 0.5], //11

  [0, 2.25, 0], // 12
  [1.125, 2.025, 0], // 13
  [2.025, 1.125, 0], //14
  [2.25, 0, 0], //15
  [2.025, -1.125, 0], //16
  [1.125, -2.025, 0], //17
  [0, -2.25, 0], //18
  [-1.125, -2.025, 0], //19
  [-2.025, -1.125, 0], //20
  [-2.25, 0, 0], //21
  [-2.025, 1.125, 0], //22
  [-1.125, 2.025, 0], //23

  [0, 0.5, -4], // 24
  [0.25, 0.45, -4], // 25
  [0.45, 0.25, -4], //26
  [0.5, 0, -4], //27
  [0.45, -0.25, -4], //28
  [0.25, -0.45, -4], //29
  [0, -0.5, -4], //30
  [-0.25, -0.45, -4], //31
  [-0.45, -0.25, -4], //32
  [-0.5, 0, -4], //33
  [-0.45, 0.25, -4], //34
  [-0.25, 0.45, -4], //35

  [0, 0.91, -5], // 36
  [0.4875, 0.8775, -5], // 37
  [0.8775, 0.4875, -5], //38
  [0.91, 0, -5], //39
  [0.8775, -0.4875, -5], //40
  [0.4875, -0.8775, -5], //41
  [0, -0.91, -5], //42
  [-0.4875, -0.8775, -5], //43
  [-0.8775, -0.4875, -5], //44
  [-0.91, 0, -5], //45
  [-0.8775, 0.4875, -5], //46
  [-0.4875, 0.8775, -5] //47
]

// prettier-ignore
const BASE_COLORS = [
   [  0,   .45,     .65], // bottom darker blue
   //[  .6,    1,     1]  // lighter blue
   [  .5,   .8,     .9]
 ]

const createSmoothPeridotGeometry = () => {
  const geometry = new BufferGeometry()

  // This effectively “flattens” our base vertices.
  const vertices = new Float32Array([
    ...BASE_VERTICES[0],
    ...BASE_VERTICES[1],
    ...BASE_VERTICES[2],
    ...BASE_VERTICES[3],
    ...BASE_VERTICES[4],
    ...BASE_VERTICES[5],
    ...BASE_VERTICES[6],
    ...BASE_VERTICES[7],
    ...BASE_VERTICES[8],
    ...BASE_VERTICES[9],
    ...BASE_VERTICES[10],
    ...BASE_VERTICES[11],

    ...BASE_VERTICES[12],
    ...BASE_VERTICES[13],
    ...BASE_VERTICES[14],
    ...BASE_VERTICES[15],
    ...BASE_VERTICES[16],
    ...BASE_VERTICES[17],
    ...BASE_VERTICES[18],
    ...BASE_VERTICES[19],
    ...BASE_VERTICES[20],
    ...BASE_VERTICES[21],
    ...BASE_VERTICES[22],
    ...BASE_VERTICES[23],

    ...BASE_VERTICES[24],
    ...BASE_VERTICES[25],
    ...BASE_VERTICES[26],
    ...BASE_VERTICES[27],
    ...BASE_VERTICES[28],
    ...BASE_VERTICES[29],
    ...BASE_VERTICES[30],
    ...BASE_VERTICES[31],
    ...BASE_VERTICES[32],
    ...BASE_VERTICES[33],
    ...BASE_VERTICES[34],
    ...BASE_VERTICES[35],

    ...BASE_VERTICES[36],
    ...BASE_VERTICES[37],
    ...BASE_VERTICES[38],
    ...BASE_VERTICES[39],
    ...BASE_VERTICES[40],
    ...BASE_VERTICES[41],
    ...BASE_VERTICES[42],
    ...BASE_VERTICES[43],
    ...BASE_VERTICES[44],
    ...BASE_VERTICES[45],
    ...BASE_VERTICES[46],
    ...BASE_VERTICES[47]
  ])

  geometry.setAttribute('position', new BufferAttribute(vertices, 3))

  const colors = new Float32Array([
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],

    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0],
    ...BASE_COLORS[0], // 23

    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],

    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1],
    ...BASE_COLORS[1]
  ])

  geometry.setAttribute('color', new BufferAttribute(colors, 3))

  // Note that this is effectively the same as the faceted version’s triangles, except that the use of
  // index values lets us _genuinely share_ a vertex (well, at least at this level) rather than copying
  // them for adjacent triangles.
  //
  // prettier-ignore
  geometry.setIndex([
     // bottom
     0, 6, 1,
     1, 6, 2,
     2, 6, 3,
     3, 6, 4,
     4, 6, 5,
     7, 6, 8,
     8, 6, 9,
     9, 6, 10,
     10, 6, 11,
     11, 6, 0,
     // slope upwards
     12, 0, 13,
     13, 0, 1,
     13, 1, 14,
     14, 1, 2,
     14, 2, 15,
     15, 2, 3,
     15, 3, 16,
     16, 3, 4,
     16, 4, 17,
     17, 4, 5,
     17, 5, 18,
     18, 5, 6,
     18, 6, 19,
     19, 6, 7,
     19, 7, 20,
     20, 7, 8,
     20, 8, 21,
     21, 8, 9,
     21, 9, 22,
     22, 9, 10,
     22, 10, 23,
     23, 10, 11,
     23, 11, 12,
     12, 11, 0,
     // neck
     24, 12, 25,
     25, 12, 13,
     25, 13, 14,
     26, 25, 14,
     26, 14, 27,
     27, 14, 15,
     27, 15, 28,
     28, 15, 16,
     28, 16, 17,
     29, 28, 17,
     29, 17, 30,
     30, 17, 18,
     30, 18, 19,
     31, 30, 19,
     31, 19, 20,
     32, 31, 20,
     32, 20, 33,
     33, 20, 21,
     33, 21, 22,
     34, 33, 22,
     34, 22, 23,
     35, 34, 23,
     35, 23, 12,
     24, 35, 12,
     //rim
    36, 24, 37,
    37, 24, 25,
    37, 25, 38,
    38, 25, 26,
    38, 26, 39,
    39, 26, 27,
    39, 27, 40,
    40, 27, 28,
    40, 28, 41,
    41, 28, 29,
    41, 29, 42,
    42, 29, 30,
    42, 30, 43,
    43, 30, 31,
    43, 31, 44,
    44, 31, 32,
    44, 32, 45,
    45, 32, 33,
    45, 33, 46,
    46, 33, 34,
    46, 34, 47,
    47, 34, 35,
    47, 35, 36,
    36, 35, 24
   ])

  // Using `setIndex` causes faces/triangles to share vertices, which then causes vertex normal computation
  // to _average out_ the normals of multiple faces, thus creating a “smoothing” effect.
  geometry.computeVertexNormals()
  return geometry
}

class HealthPotion {
  constructor(radius, position, stop) {
    const material = new MeshPhongMaterial({ vertexColors: true, opacity: 0.4, transparent: true })
    material.side = DoubleSide

    const smoothGeometry = createSmoothPeridotGeometry()
    const smoothMesh = new Mesh(smoothGeometry, material)
    smoothMesh.position.x = 1

    const crossGeometry = new BoxGeometry(2, 0.6, 0.6)
    const crossMaterial = new MeshPhongMaterial({ color: 'red' })
    const crossMesh = new Mesh(crossGeometry, crossMaterial)
    crossMesh.position.set(1.1, 0, -1.5)

    const crossMesh2 = new Mesh(crossGeometry, crossMaterial)
    crossMesh2.position.set(1.1, 0, -1.5)
    crossMesh2.rotation.y = 1.5708

    this.group = new Group()
    this.group.add(smoothMesh, crossMesh, crossMesh2)
    this.group.rotation.x = 1.5708
    this.group.scale.set(radius, radius, radius)
    this.group.position.set(position.x, position.y, position.z)

    this.velocity = { x: -0.2, y: -0.1 }
    this.maxVelocity = { y: 0.1 }
    this.radius = radius
    this.stop = stop
    this.type = 'healthPotion'
  }

  move(position) {
    if (this.group.position.y < -1) {
      this.velocity.y = Math.min(this.velocity.y + 0.001, this.maxVelocity.y)
    } else if (this.group.position.y > 2) {
      this.velocity.y = Math.max(this.velocity.y - 0.001, -this.maxVelocity.y)
    }
    this.group.position.x += this.velocity.x
    this.group.position.y += this.velocity.y
  }
}

export default HealthPotion
